import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface AuthPanelProps {
  readonly title: string;
  readonly description?: string | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

/**
 * The frame for signing in, enrolling a factor and accepting an invitation.
 *
 * These were a heading and a form at the top-left of an otherwise empty page, the form as wide as a
 * paragraph (M5.5-01): the first screen anybody sees, and the least considered. A panel the width of
 * the form, centred, says "this is the one thing to do here" without a word of copy.
 *
 * Centred vertically only from `md` up: on a phone the keyboard takes the lower half of the screen,
 * and a form centred in the whole height is a form under the keyboard.
 */
export function AuthPanel({ title, description, children, className }: AuthPanelProps) {
  return (
    <div className="flex flex-1 items-start justify-center px-(--space-lg) py-(--space-3xl) md:items-center">
      <section
        aria-labelledby="auth-panel-title"
        className={cn(
          'flex w-full max-w-(--width-panel) flex-col gap-(--space-lg)',
          'rounded-(--radius-lg) border border-(--color-border) bg-(--color-surface) p-(--space-xl)',
          'shadow-(--shadow-raised)',
          className,
        )}
      >
        <header className="flex flex-col gap-(--space-xs)">
          <h1
            id="auth-panel-title"
            className="text-(length:--font-size-title) font-semibold text-(--color-text-primary)"
          >
            {title}
          </h1>
          {description === undefined ? null : (
            <p className="text-(length:--font-size-body) text-(--color-text-secondary)">
              {description}
            </p>
          )}
        </header>
        {children}
      </section>
    </div>
  );
}
